
import { useState } from "react"
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button as MuiButton,
  Button, 
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip
} from "@mui/material"
import * as XLSX from "xlsx"
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  FirstPage,
  LastPage,
  CheckCircle
} from "@mui/icons-material"
import axios from "axios"
import api from "../../components/commonComponents/axiosSetup"
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"
import { BASE_URL } from "../../config"

const OverviewPage = ({ fileData, fileName, uploadedFile, onSubmit, onPrevious, onCancel, accountName, actionType }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [recordsPerPage, setRecordsPerPage] = useState(10)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [snackbarSeverity, setSnackbarSeverity] = useState("success")


  const navigate = useNavigate()
  
// Replace your validation logic with this:

// Validation functions
const hasSpecialChars = (str) => {
  if (str == null || String(str).trim() === "") return false; // Ignore empty cells
  // Only consider as special chars if it contains letters or symbols (not just numbers)
  return /[a-zA-Z\W_]/.test(String(str).trim());
};

const isValidMsisdnLength = (msisdn) => {
  if (!msisdn) return false;
  const cleanNumber = String(msisdn).trim().replace(/\D/g, '');
  return cleanNumber.length >= 12 && cleanNumber.length <= 15;
};

const isValidMobileNumber = (number) => {
  if (!number) return true; // Skip empty cells
  const cleanNumber = String(number).trim();
  if (cleanNumber === "") return true; // Skip empty strings
  // Check if it contains only digits
  return /^\d+$/.test(cleanNumber);
};

const msisdnCounts = {};
(fileData || []).forEach(row => {
  const msisdn = (row.msisdn || row.Msisdn || "").toString().trim();
  if (msisdn) {
    msisdnCounts[msisdn] = (msisdnCounts[msisdn] || 0) + 1;
  }
});
const duplicateMsisdns = Object.keys(msisdnCounts).filter(msisdn => msisdnCounts[msisdn] > 1);
// Check all validations and return reason if invalid
const validateRow = (row) => {
  if (!row) return { isValid: true };
  
  // Check MSISDN length
  const msisdnValue = row.msisdn || row.Msisdn;
  const groupNameValue = row.group_name || row.Group_name;
  if (msisdnValue && !isValidMsisdnLength(msisdnValue)) {
    return { 
      isValid: false, 
      reason: `MSISDN length invalid: should be 12-15 digits (found: ${String(msisdnValue).trim().length}) digits`
    };
  }

  if (msisdnValue && (!groupNameValue || String(groupNameValue).trim() === "")) {
    return {
      isValid: false,
      reason: "Group name cannot be blank when MSISDN is provided"
    };
  }

    if (msisdnValue && duplicateMsisdns.includes(String(msisdnValue).trim())) {
    return {
      isValid: false,
      reason: `Duplicate MSISDN found: "${msisdnValue}"`
    };
  }
  
  // Check for special characters in msisdn field only
  if (msisdnValue && hasSpecialChars(msisdnValue)) {
    return { 
      isValid: false, 
      reason: `Special characters found in msisdn: "${msisdnValue}"`
    };
  }
  
  // Check if msisdn is a valid mobile number
  if (msisdnValue && !isValidMobileNumber(msisdnValue)) {
    return {
      isValid: false,
      reason: `Invalid mobile number format in msisdn: "${msisdnValue}"`
    };
  }
  
  // Don't validate group_name for special characters - it can contain text
  
  return { isValid: true };
};

// Process data with enhanced validations
const processedData = fileData?.map(row => {
  const validation = validateRow(row);
  
  return {
    ...row,
    status: validation.isValid ? "success" : "invalid",
    invalidReason: validation.isValid ? "" : validation.reason
  };
}) || [];

  // Calculate statistics
  const totalRecords = processedData?.length || 0
  const validRecords = processedData?.filter((row) => row.status === "success")?.length || 0
  const invalidRecords = processedData?.filter((row) => row.status === "invalid" || row.status === "error")?.length || 0

  // Pagination logic
  const totalPages = Math.ceil(totalRecords / recordsPerPage)
  const startIndex = (currentPage - 1) * recordsPerPage
  const endIndex = startIndex + recordsPerPage
  const currentData = processedData?.slice(startIndex, endIndex) || []

  const handleSubmitClick = () => {
     if (invalidRecords > 0) {
    setSnackbarMessage(`Cannot submit: ${invalidRecords} invalid records found. Please fix all errors before submitting.`);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
    return; // Prevent submission
  }
    setShowConfirmDialog(true)
  }
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }
  
  // Function to download invalid records as Excel
  const downloadInvalidRecords = () => {
    // Filter only invalid records
    const invalidRecords = processedData.filter(record => record.status === "invalid");
    
    if (invalidRecords.length === 0) {
      setSnackbarMessage("No invalid records to download");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      return;
    }
    
    try {
      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Convert the data to a format suitable for Excel, including the invalidReason column
      const dataForExcel = invalidRecords.map(record => {
        const { status, ...recordWithoutStatus } = record;
        return {
          ...recordWithoutStatus,
          invalidReason: record.invalidReason || "Unknown validation error"
        };
      });
      
      // Create a worksheet from the data
      const ws = XLSX.utils.json_to_sheet(dataForExcel);
      
      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, "Invalid Records");
      
      // Generate a download link
      XLSX.writeFile(wb, `invalid_records_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      setSnackbarMessage("Invalid records downloaded successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error downloading invalid records:", error);
      setSnackbarMessage("Failed to download invalid records");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }

  const handleSubmit = async () => {
    if (!fileData || fileData.length === 0 || !uploadedFile) {
      setSnackbarMessage("No data or file to submit")
      setSnackbarSeverity("error")
      setSnackbarOpen(true)
      return
    }
    
    setIsSubmitting(true)
    
    try {
      // Create a FormData object to send the file
      const formData = new FormData()
      
      // Append the file
      formData.append('file', uploadedFile)
      
      // Append other data as JSON
      formData.append('accountName', accountName)
      formData.append('actionType', actionType)
      
      // If you want to include the processed data as well, 
      // you can stringify it and append it to the FormData
    //   formData.append('processedData', JSON.stringify(fileData))
      
      // Make the API call - replace with your actual API endpoint
      const response = await api.post(
        `${BASE_URL}bulk/whitelistUpload`, 
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      )
      
      setSubmitSuccess(true)
      toast.success("Data submitted successfully")
      navigate('/bulkWhitelist')
      setSnackbarMessage("Data submitted successfully")
      setSnackbarSeverity("success")
      setSnackbarOpen(true)
      
      // Call parent onSubmit if provided
      if (typeof onSubmit === 'function') {
        onSubmit(response.data)
      }
      
    } catch (error) {
      console.error("Error submitting data:", error)
      setSubmitError(error.response?.data?.message || "Failed to submit data")
      setSnackbarMessage(error.response?.data?.message || "Failed to submit data")
      setSnackbarSeverity("error")
      setSnackbarOpen(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      {/* All Status Cards in one row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 4 }}>
        {/* File Name */}
        <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
          <Typography variant="body1" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {fileName || "sms_whitelist_sample..."}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            File Name
          </Typography>
        </Paper>

        {/* Success Status */}
        <Card sx={{ 
          backgroundColor: invalidRecords > 0 ? '#ffebee' : '#fff8e1', 
          border: invalidRecords > 0 ? '1px solid #ffcdd2' : '1px solid #ffecc7' 
        }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ 
              color: invalidRecords > 0 ? '#c62828' : '#b28704', 
              fontWeight: 600 
            }}>
              {invalidRecords > 0 ? 'Warning' : 'Success'}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: invalidRecords > 0 ? '#c62828' : '#b28704' 
            }}>
              {invalidRecords > 0 
                ? `${invalidRecords} invalid records found` 
                : 'Format Validation Status'
              }
            </Typography>
          </CardContent>
        </Card>

        {/* Total Records */}
        <Card sx={{ backgroundColor: '#e3f2fd', border: '1px solid #bbdefb' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ color: '#0d47a1', fontWeight: 700 }}>
              {totalRecords}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1976d2' }}>
              Total Records
            </Typography>
          </CardContent>
        </Card>

        {/* Valid Records */}
        <Card sx={{ backgroundColor: '#e3f2fd', border: '1px solid #bbdefb' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ color: '#0d47a1', fontWeight: 700 }}>
              {validRecords}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1976d2' }}>
              Valid Records
            </Typography>
          </CardContent>
        </Card>
        
        {/* Invalid Records */}
        <Tooltip title={invalidRecords > 0 ? "Click to download invalid records" : "No invalid records to download"}>
          <Card 
            sx={{ 
              backgroundColor: '#ffebee', 
              border: '1px solid #ffcdd2',
              cursor: invalidRecords > 0 ? 'pointer' : 'default',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: invalidRecords > 0 ? '0px 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                transform: invalidRecords > 0 ? 'translateY(-2px)' : 'none',
              }
            }}
            onClick={invalidRecords > 0 ? downloadInvalidRecords : undefined}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h5" sx={{ color: '#c62828', fontWeight: 700 }}>
                {invalidRecords}
              </Typography>
              <Typography variant="body2" sx={{ color: '#e53935' }}>
                Invalid Records {invalidRecords > 0 && '(click to download)'}
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>
      </Box>

      {/* Data Table */}
      <Paper sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Bulk Whitelist Overview
          </Typography>
        </Box>

        <Box sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'var(--bg)' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>MSISDN</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>Group Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>Issue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentData.map((row, index) => (
                <TableRow 
                  key={index} 
                  sx={{ 
                    backgroundColor: row.status === "invalid" 
                      ? 'rgba(255, 205, 210, 0.5)' // More visible light red background for invalid rows
                      : index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                    '&:hover': {
                      backgroundColor: row.status === "invalid" 
                        ? 'rgba(255, 205, 210, 0.7)' // Darker on hover
                        : '#f0f0f0'
                    }
                  }}
                >
                  <TableCell>{row.msisdn || row.Msisdn}</TableCell>
                  <TableCell>{row.group_name || row.Group_name || ""}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.status || "success"} 
                      size="small" 
                      sx={{ 
                        backgroundColor: row.status === "invalid" ? '#ffebee' : '#e8f5e9', 
                        color: row.status === "invalid" ? '#c62828' : '#2e7d32',
                        borderColor: row.status === "invalid" ? '#ffcdd2' : '#c8e6c9' 
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {row.status === "invalid" && (
                      <Tooltip title={row.invalidReason || "Invalid record"}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#c62828',
                            maxWidth: 150,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {row.invalidReason || "Invalid record"}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalRecords}
          page={currentPage - 1}
          rowsPerPage={recordsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(e, newPage) => setCurrentPage(newPage + 1)}
          onRowsPerPageChange={(e) => {
            setRecordsPerPage(parseInt(e.target.value, 10));
            setCurrentPage(1);
          }}
          backIconButtonProps={{ size: 'small' }}
          nextIconButtonProps={{ size: 'small' }}
          ActionsComponent={(props) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MuiButton 
                size="small" 
                onClick={() => setCurrentPage(1)} 
                disabled={currentPage === 1}
                variant="outlined"
                sx={{ minWidth: 'auto', p: '4px' }}
              >
                <FirstPage fontSize="small" />
              </MuiButton>
              <MuiButton 
                size="small" 
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                variant="outlined"
                sx={{ minWidth: 'auto', p: '4px' }}
              >
                <KeyboardArrowLeft fontSize="small" />
              </MuiButton>
              <MuiButton 
                size="small" 
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                variant="outlined"
                sx={{ minWidth: 'auto', p: '4px' }}
              >
                <KeyboardArrowRight fontSize="small" />
              </MuiButton>
              <MuiButton 
                size="small" 
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                variant="outlined"
                sx={{ minWidth: 'auto', p: '4px' }}
              >
                <LastPage fontSize="small" />
              </MuiButton>
            </Box>
          )}
        />
      </Paper>

      {/* Action buttons */}
      <Box sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        mt: 3
      }}>
        <Button
          variant="outlined"
          onClick={onPrevious}
          sx={{
            px: 4,
            textTransform: "none",
            borderColor: "#2D3748",
            color: "#2D3748",
          }}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitClick}
          disabled={isSubmitting}
          sx={{
            px: 4,
            textTransform: "none",
            backgroundColor: "var(--bg)",
            "&:hover": {
              backgroundColor: "var(--bg)",
            },
          }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              submitting...
            </>
          ) : (
            "submit"
          )}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <DialogTitle>Confirm Submission</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to submit {fileData?.length || 0} records from file: {uploadedFile?.name || "Unknown file"}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setShowConfirmDialog(false);
              handleSubmit();
            }} 
            variant="contained" 
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default OverviewPage
